import React, { useEffect, useState } from 'react';
import { Typography, Tooltip, debounce } from '@mui/material';
import { styled, keyframes, Box } from '@mui/system';
import { COLOR_PRIMARY_2, COLOR_WARNING } from 'src/utils/colors';
import { exchangeRates } from 'src/utils/const';
import { convertCurrency, formatCurrency } from 'src/utils/functions';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import SaveBoxPointer from './SaveBoxPointer';
const buzzAnimation = keyframes`
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 70% { transform: translateX(-2px); }
  40%, 60% { transform: translateX(1px); }
  50% { transform: translateX(-1px); }
`;

const MarkerWrapper = styled('div')({
  width: 80,
  height: 80,
  transform: 'translate(-50%, -50%)',
  cursor: 'pointer',
  // flex-center the bubble inside:
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Bubble = styled('div')({
  position: 'relative',
  backgroundColor: COLOR_PRIMARY_2,
  color: '#fff',
  borderRadius: '12px',
  padding: '4px 8px',
  textAlign: 'center',
  width: '60px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  '&.hovered': {
    animation: `${buzzAnimation} 0.5s ease`,
  },
});

const Pointer = styled('div')({
  position: 'absolute',
  bottom: -6,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  borderTop: `6px solid ${COLOR_PRIMARY_2}`,
});

const SmallBadge = styled('div')({
  position: 'absolute',
  top: -14,
  right: -10,
  backgroundColor: '#fff',
  color: COLOR_PRIMARY_2,
  borderRadius: '9999px',
  padding: '2px 6px',
  fontSize: '0.75rem',
  fontWeight: 600,
  border: `1px solid ${COLOR_PRIMARY_2}`,
});

const ClusterPointer = ({
  marketID,
  lat,
  lng,
  supply,
  performanceMetrics,
  clusterKey,
  currency,
  shouldFade,
  color,
  savedClusters,
  setSavedClusters,
}: {
  marketID: string;
  lat: number;
  lng: number;
  supply: number;
  performanceMetrics: {
    adr: number;
    occupancyRate: number;
    revPar: number;
    supply: number;
    revenue: number;
  };
  clusterKey: string;
  currency: string;
  shouldFade: boolean;
  color: string;
  savedClusters: string[];
  setSavedClusters: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const normalizedClusterKey = clusterKey === 'occupancy_rate' ? 'occupancyRate' : clusterKey;
  const resolvedMetricKey = ['supply', 'occupancyRate', 'adr'].includes(normalizedClusterKey)
    ? normalizedClusterKey
    : 'supply';
  const metric = performanceMetrics[resolvedMetricKey];
  const metricLabelMap = {
    supply: 'Active Listings',
    occupancyRate: 'Occupancy Rate',
    adr: 'Nightly Rate',
  };
  const metricLabel = metricLabelMap[resolvedMetricKey];
  const currencySymbol = exchangeRates[currency].symbol;
  const clusterId = `${clusterKey}-${lat}-${lng}`;
  const isThisClusterSaved = savedClusters[clusterId];
  const currentClusterName = savedClusters[clusterId]
    ? String(savedClusters[clusterId].name || '')
    : '';

  const [isHovered, setIsHovered] = useState(false);

  return (
    <MarkerWrapper onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <Bubble
        className={isHovered ? 'hovered' : ''}
        style={{
          backgroundColor: color,
          width: shouldFade && !isHovered ? '15px' : '60px',
        }}
      >
        <Typography
          style={{
            fontSize: '0.65rem',
            lineHeight: shouldFade && !isHovered ? 0.25 : 1,
            visibility: shouldFade && !isHovered ? 'hidden' : 'visible',
          }}
        >
          {metricLabel}
        </Typography>

        {metric !== null && metric !== undefined ? (
          <Typography
            style={{
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: shouldFade && !isHovered ? 0.3 : 1.2,
              visibility: shouldFade && !isHovered ? 'hidden' : 'visible',
            }}
          >
            {resolvedMetricKey == 'supply'
              ? metric
              : resolvedMetricKey == 'occupancyRate'
              ? `${metric}%`
              : `${currencySymbol}${formatCurrency(String(convertCurrency(currency, metric)))}`}
          </Typography>
        ) : (
          <Typography
            style={{
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: shouldFade && !isHovered ? 0.3 : 1.2,
              visibility: shouldFade && !isHovered ? 'hidden' : 'visible',
            }}
          >
            N/A
          </Typography>
        )}

        <Pointer
          style={{
            borderTop: `6px solid ${color}`,
          }}
        />

        {isThisClusterSaved && (
          <Box
            style={{
              position: 'absolute',
              top: -7,
              right: -6,
              backgroundColor: COLOR_WARNING,
              color: '#fff',
              borderRadius: '9999px',
              fontWeight: 600,
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StarRoundedIcon sx={{ fontSize: '12px' }} />
          </Box>
        )}
      </Bubble>
    </MarkerWrapper>
  );
};

export default ClusterPointer;
